/** Typed API surface — one function per backend endpoint (see BACKEND.md).
 * These are the real connections: point VITE_API_URL at the server and every
 * feature swaps from localStorage to live data. */
import { apiFetch, apiUpload, API_URL, getToken, ApiError } from './client';
import type {
  Booking, CareerJob, CmsBanner, CmsReel, CmsBlog, CmsBlogSummary, CmsFaq, CmsPolicy, CmsPolicySummary, CmsTestimonial, Coupon, Event, Featured, FeaturedSubscription, HelpTicket, HelpTicketReply,
  Invoice, JobApplication, LineupProfile, MarketingAnalytics, MarketingPeriodAnalytics, MarketingOrder, MarketingRates, PlacementRates, MarketingSubscription, Organizer, PayMethod, PaymentProfile,
  Person, PersonDetail, PromoterProfile, User, Venue, WaitlistEntry,
} from '../types';
import type { CartRecord, GuestReview, PromoterGuest, Referral, SubPromoter, WalletTx } from '../store/AppContext';

// ---------- auth ----------
export const auth = {
  requestOtp: (phone: string) => apiFetch<{ requestId: string; devCode?: string; existingName?: string }>('/auth/otp', { body: { phone } }),
  verifyOtp: (requestId: string, code: string, name?: string) =>
    apiFetch<{ token: string; user: User; isNew: boolean }>('/auth/verify', {
      // Only matters for a brand-new signup (see AuthService.verifyOtp) —
      // lets a new User row get created with the real cookie choice
      // instead of always defaulting to false.
      body: { requestId, code, name, marketingConsent: localStorage.getItem('pb_cookie_consent') === 'accepted' },
    }),
  me: () => apiFetch<User>('/me'),
  updateMe: (patch: Partial<User>) => apiFetch<User>('/me', { method: 'PATCH', body: patch }),
  upload: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return apiUpload<{ url: string }>('/me/upload', form);
  },
  logout: () => apiFetch<void>('/auth/logout', { method: 'POST' }),
  requestPhoneChange: (newPhone: string) => apiFetch<{ requestId: string; devCode?: string }>('/me/phone/request-change', { body: { newPhone } }),
  confirmPhoneChange: (requestId: string, code: string) => apiFetch<User>('/me/phone/confirm-change', { body: { requestId, code } }),
  /** Awards (or, if already claimed, just re-returns) the one-time 10%-off
   * coupon for finishing the soft-required "complete your profile" step. */
  claimProfileReward: () => apiFetch<{ code: string; maxDiscount: number; validTill: string; alreadyClaimed: boolean }>('/me/complete-profile-reward', { method: 'POST' }),
  /** Same real backend contract (POST /me/delete) as the guest/organizer RN
   * apps' DeleteAccountScreen — this is the web, no-app-required self-service
   * equivalent Google's Play Console Data Safety form requires a public URL
   * for (it won't accept an in-app-only deletion flow). */
  deleteAccount: () => apiFetch<void>('/me/delete', { method: 'POST' }),
};

// ---------- identity & role verification ----------
// Guest ID verification is automatic (OCR + face-match). Organizer/promoter/
// lineup/venue applications are always manual — a human on the team reviews
// every one in the admin panel; submitting never grants the role directly.
// See BACKEND.md "Identity & KYC".
export const kyc = {
  submitGuest: (idDoc: File, selfie: File) => {
    const form = new FormData();
    form.append('idDoc', idDoc);
    form.append('selfie', selfie);
    return apiUpload<{ status: 'approved' | 'rejected'; score: number; reason?: string }>('/kyc/guest', form);
  },
  submitRole: (kind: 'organizer' | 'promoter' | 'lineup' | 'venue', payload: Record<string, unknown>, documents: File[]) => {
    const form = new FormData();
    form.append('kind', kind);
    form.append('payload', JSON.stringify(payload));
    documents.forEach((f) => form.append('documents', f));
    return apiUpload<{ status: 'pending'; user: User }>('/kyc/role', form);
  },
  // documents/rejectedDocTypes/payload all come through with no extra
  // params — needed by the rejected-resubmission flow (OrganizerVerification.tsx
  // and its counterparts) to show exactly what's wrong and pre-fill the rest.
  myStatus: () => apiFetch<{
    id: string; kind: string; status: string; createdAt: string; reviewNote?: string;
    documents: { type: string; path: string }[];
    rejectedDocTypes: string[];
    payload: Record<string, unknown>;
  }[]>('/kyc/me'),
  quickSignupOrganizer: (payload: {
    brand: string;
    city: string; state?: string; country?: string; pincode?: string;
    types: string[];
    socialLinks?: { instagram?: string; facebook?: string; other?: string[] };
  }) => apiFetch<{ status: 'approved'; user: User }>('/kyc/organizer/quick-signup', { body: payload }),
  /** Identity verification only (Aadhaar+selfie, or registration+owner's
   * Aadhaar+selfie for a firm) — entirely separate from payout details, see
   * organizer.createPaymentProfile below. `docs` order must match
   * `payload.docLabels` order — see OrganizerVerification.tsx. */
  submitOrganizerVerification: (
    payload: {
      entityType: 'individual' | 'firm';
      contactName: string; contactPhone: string; contactEmail: string;
      contactRole: 'Owner' | 'Manager' | 'Accountant' | 'Other'; contactRoleOther?: string;
      docLabels: string[];
      // Resubmitting after a rejection — carries forward every required
      // document NOT freshly re-uploaded this time (i.e. not flagged in
      // rejectedDocTypes) by copying it straight from this prior submission
      // server-side. See KycService.submitOrganizerVerification.
      previousSubmissionId?: string;
    },
    docs: File[],
  ) => {
    const form = new FormData();
    form.append('payload', JSON.stringify(payload));
    docs.forEach((f) => form.append('documents', f));
    return apiUpload<{ id: string; status: string }>('/kyc/organizer/verification', form);
  },
  quickSignupPromoter: (payload: {
    brand: string;
    city: string; state?: string; country?: string; pincode?: string;
    bio: string; links?: string; audience?: string;
  }) => apiFetch<{ status: 'approved'; user: User }>('/kyc/promoter/quick-signup', { body: payload }),
  /** 2 files, in order: id document, selfie. */
  submitPromoterVerification: (docs: File[]) => {
    const form = new FormData();
    docs.forEach((f) => form.append('documents', f));
    return apiUpload<{ id: string; status: string }>('/kyc/promoter/verification', form);
  },
  quickSignupLineup: (payload: {
    name: string; category: string;
    city: string; state?: string; country?: string; pincode?: string;
    bio: string; links?: string[]; logoUrl?: string;
  }) => apiFetch<{ status: 'approved'; user: User }>('/kyc/lineup/quick-signup', { body: payload }),
  /** 2 files, in order: id document, selfie. */
  submitLineupVerification: (docs: File[]) => {
    const form = new FormData();
    docs.forEach((f) => form.append('documents', f));
    return apiUpload<{ id: string; status: string }>('/kyc/lineup/verification', form);
  },
  /** 2 files: license, address proof — tagged via payload.docLabels in the
   * same order they're attached. Venue's own signup no longer takes any
   * documents at all — see venuePartner.onboard below. */
  submitVenueVerification: (
    payload: {
      contactName: string; contactPhone: string; contactEmail: string;
      contactRole: 'Owner' | 'Manager' | 'Accountant' | 'Other'; contactRoleOther?: string;
      docLabels: string[];
    },
    docs: File[],
  ) => {
    const form = new FormData();
    form.append('payload', JSON.stringify(payload));
    docs.forEach((f) => form.append('documents', f));
    return apiUpload<{ id: string; status: string }>('/kyc/venue/verification', form);
  },
};

/** Captures an incomplete role application as a draft Lead (Admin > Leads),
 * so it doesn't just disappear if someone verifies OTP and leaves, or fills
 * part of the form and abandons it — see useDraftLead.ts for the two call
 * sites (on page load, and debounced as the form fills in). Best-effort:
 * every call site swallows failures, never blocks the real onboarding flow. */
export const leadDraft = {
  capture: (role: 'organizer' | 'promoter' | 'lineup' | 'venue', fields: { name?: string; city?: string; eventType?: string; utmSource?: string; utmMedium?: string }) =>
    apiFetch<void>('/leads/draft', { body: { role, ...fields } }),
};

// ---------- discovery ----------
export const catalog = {
  events: (q: { city?: string; cat?: string; sub?: string; search?: string; sort?: string; organizerId?: string; venueId?: string; includePast?: boolean }) => apiFetch<Event[]>('/events', { query: q }),
  event: (slug: string, preview?: string) => apiFetch<Event>(`/events/${slug}`, { query: { preview } }),
  venues: (city?: string) => apiFetch<Venue[]>('/venues', { query: { city } }),
  venue: (id: string) => apiFetch<Venue>(`/venues/${id}`),
  venueSeo: (id: string) => apiFetch<{ title: string; description: string; keywords: string }>(`/venues/${id}/seo`),
  organizers: (city?: string) => apiFetch<Organizer[]>('/organizers', { query: { city } }),
  organizer: (id: string) => apiFetch<Organizer>(`/organizers/${id}`),
  organizerSeo: (id: string) => apiFetch<{ title: string; description: string; keywords: string }>(`/organizers/${id}/seo`),
  promoters: (city?: string) => apiFetch<PromoterProfile[]>('/promoters', { query: { city } }),
  promoter: (slug: string) => apiFetch<PromoterProfile>(`/promoters/${slug}`),
  promoterSeo: (id: string) => apiFetch<{ title: string; description: string; keywords: string }>(`/promoters/${id}/seo`),
  lineups: (city?: string) => apiFetch<LineupProfile[]>('/lineups', { query: { city } }),
  lineup: (slug: string) => apiFetch<LineupProfile>(`/lineups/${slug}`),
  lineupSeo: (id: string) => apiFetch<{ title: string; description: string; keywords: string }>(`/lineups/${id}/seo`),
  people: (city?: string) => apiFetch<Person[]>('/people', { query: { city } }),
  person: (username: string) => apiFetch<PersonDetail>(`/people/${username}`),
  featured: (city: string) => apiFetch<Featured[]>('/featured', { query: { city } }),
  categories: () => apiFetch<{ name: string; icon: string; subs: string[] }[]>('/categories'),
  cities: () => apiFetch<{ name: string; icon?: string; top: boolean; state?: string; events: number; venues: number; organizers: number }[]>('/cities'),
  venueTypes: () => apiFetch<{ name: string; icon?: string; events: number }[]>('/venue-types'),
  amenities: () => apiFetch<{ name: string; icon?: string }[]>('/amenities'),
  search: (q: string) => apiFetch<{ label: string; type: string; to: string }[]>('/search', { query: { q } }),
  trending: () => apiFetch<string[]>('/search/trending'),
  reels: (city?: string) => apiFetch<CmsReel[]>('/reels', { query: { city } }),
};

// ---------- CMS content (guest-facing reads of admin's Content section) ----------
export const content = {
  banners: (city?: string) => apiFetch<CmsBanner[]>('/banners', { query: { city } }),
  testimonials: (featuredOnly?: boolean) => apiFetch<CmsTestimonial[]>('/testimonials', { query: { featured: featuredOnly } }),
  faqs: (audience?: 'guests' | 'organizers') => apiFetch<CmsFaq[]>('/faqs', { query: { audience } }),
  policies: () => apiFetch<CmsPolicySummary[]>('/policies'),
  policy: (slug: string) => apiFetch<CmsPolicy>(`/policies/${slug}`),
  blogs: () => apiFetch<CmsBlogSummary[]>('/blogs'),
  blog: (id: string) => apiFetch<CmsBlog>(`/blogs/${id}`),
};

// ---------- real organizer/venue reviews (guest-facing) ----------
export interface OrgReview {
  id: string;
  author: string;
  rating: number;
  text: string;
  organizerId: string;
  eventTitle?: string;
  date: string;
}
export interface VenueReview {
  id: string;
  author: string;
  rating: number;
  text: string;
  venueId: string;
  date: string;
}
export const socialReviews = {
  organizer: (id: string) => apiFetch<OrgReview[]>(`/organizers/${id}/reviews`),
  addOrganizerReview: (id: string, rating: number, text: string, eventId?: string) => apiFetch<OrgReview>(`/organizers/${id}/reviews`, { body: { rating, text, eventId } }),
  venue: (id: string) => apiFetch<VenueReview[]>(`/venues/${id}/reviews`),
  addVenueReview: (id: string, rating: number, text: string) => apiFetch<VenueReview>(`/venues/${id}/reviews`, { body: { rating, text } }),
};

// ---------- bookings, holds, waitlist ----------
export interface BookingQuote {
  subtotal: number;
  fee: number;
  // GST (real GSTIN activated 2026-09-21) on the booking fee only — both 0
  // whenever PlatformSettings.gstEnabled is false, unchanged from before.
  // igstAmount>0 means an inter-state supply (single IGST line); 0 means
  // intra-state, split into cgstAmount+sgstAmount instead — same
  // commonGst.ts convention the Invoice PDF uses.
  gstPct?: number;
  gst?: number;
  cgstAmount?: number;
  sgstAmount?: number;
  igstAmount?: number;
  discount: number;
  walletCreditUsed: number;
  total: number;
  // present only when this hold is attributed to a promoter with a
  // nonzero revenue-share % set for this event — see BookingsService.priceHold
  promoterMarkupApplies?: boolean;
  promoterShare?: number;
  platformShare?: number;
  // PhonePe redirects the whole browser to this hosted checkout page — no
  // embedded widget, no client key needed (unlike Razorpay's orderId+keyId
  // pair). phonepeMerchantOrderId is the same value as the holdId the quote
  // was made for; carried separately so the return page doesn't need to
  // parse it back out of the URL by convention.
  phonepeRedirectUrl?: string;
  phonepeMerchantOrderId?: string;
}
export interface AvailableCoupon {
  code: string;
  type: 'percent' | 'flat';
  value: number;
  maxDiscount: number | null;
  description: string | null;
}
export interface CreateBookingInput {
  holdId: string;
  mainGuest: string;
  // Real 2026-08-27 bug: Checkout.tsx collected and required this but
  // never actually sent it — confirmed against production data, every
  // extra guest had gender saved, the main attendee never did.
  mainGuestGender?: string;
  // Only ever sent when the event is 18+/21+ and the account doesn't
  // already have a qualifying age on file — see requiredAgeFor.
  age?: number;
  whatsapp: string;
  guests?: { name: string; gender?: string; whatsapp?: string }[];
  couponCode?: string;
  walletCredit?: number;
  promoterRef?: string;
  promoterVia?: string;
  payMethodId?: string;
  razorpay?: { orderId: string; paymentId: string; signature: string };
  // No client-reported signature for PhonePe — the backend independently
  // confirms real payment itself via getOrderStatus, this is just which
  // order to check. See PhonePeService's doc comment.
  phonepe?: { merchantOrderId: string };
}
export const bookings = {
  hold: (eventId: string, qty: Record<string, number>) => apiFetch<{ holdId: string; expiresAt: string }>('/bookings/hold', { body: { eventId, qty } }),
  availableCoupons: (eventId: string) => apiFetch<AvailableCoupon[]>('/bookings/coupons', { query: { eventId } }),
  quote: (holdId: string, couponCode?: string, walletCredit?: number, promoterRef?: string) =>
    apiFetch<BookingQuote>('/bookings/quote', { body: { holdId, couponCode, walletCredit, promoterRef } }),
  // Fire-and-forget snapshot of the attendee-details form, sent right
  // before the Razorpay widget opens — lets the payment webhook finish the
  // booking if this browser tab never comes back (a real incident: a UPI
  // app-switch backgrounded/killed the tab mid-payment, Razorpay captured
  // the money, but the booking never got created). Never blocks or fails
  // the real checkout — see Checkout.tsx's own .catch(() => {}) on this call.
  // keepalive: true — this is always fired right before window.location.href
  // navigates away to PhonePe (see Checkout.tsx), so the request must
  // survive the page unloading, not just "best effort" fire-and-forget.
  prepare: (input: Omit<CreateBookingInput, 'razorpay'>) => apiFetch<{ ok: true }>('/bookings/prepare', { body: input, keepalive: true }),
  // Checkout.tsx's PhonePe-return resume calls this when sessionStorage
  // (where prepare()'s payload was ALSO meant to be stashed client-side)
  // comes back empty — recovers the real PhonePe order id for this hold so
  // the guest can be shown the attendee form again instead of hitting a
  // dead end.
  pendingOrder: (holdId: string) => apiFetch<{ merchantOrderId: string | null; bookingId: string | null }>(`/bookings/pending-order/${encodeURIComponent(holdId)}`),
  create: (input: CreateBookingInput) => apiFetch<Booking>('/bookings', { body: input }),
  list: () => apiFetch<Booking[]>('/bookings'),
  // booking ids contain a literal "#" (e.g. "#TKT-12345"), which the URL
  // parser treats as a fragment separator if left unencoded — silently
  // truncating the path. Must percent-encode; server-side decodes it back.
  cancel: (id: string, refundTo: 'wallet' | 'source') => apiFetch<Booking>(`/bookings/${encodeURIComponent(id)}/cancel`, { body: { refundTo } }),
  resend: (id: string) => apiFetch<{ ok: true }>(`/bookings/${encodeURIComponent(id)}/resend`, { method: 'POST' }),
  // Public, no auth — polled by PayComplete.tsx for an org-initiated
  // offline payment-link booking, where the guest has no session at all.
  offlinePaymentLinkStatus: (holdId: string) => apiFetch<{ status: 'paid' | 'pending' | 'failed' | 'unknown' }>(`/pay/status/${encodeURIComponent(holdId)}`),
  // real route is /bookings/check-in with the QR's signed token in the body
  // — not /bookings/:id/check-in like this used to declare (that endpoint
  // never existed; nothing called this wrapper before now).
  checkIn: (token: string, eventId?: string) => apiFetch<Booking>('/bookings/check-in', { body: { token, eventId } }),
  waitlistJoin: (eventId: string) => apiFetch<WaitlistEntry>(`/events/${eventId}/waitlist`, { method: 'POST' }),
  waitlist: (eventId: string) => apiFetch<WaitlistEntry[]>(`/events/${eventId}/waitlist`),
  // Public, no auth — the link an offline booking's WhatsApp confirmation
  // carries (see BookingsService.ticketView). The token itself (already a
  // signed, booking-specific JWT) is the access credential.
  ticketView: (token: string) =>
    apiFetch<{
      id: string; mainGuest: string; tierName: string; qty: number; total: number; status: string; checkedIn: boolean; qrToken: string;
      coverCharge: number;
      guests: { name: string; checkedIn: boolean; gender?: string }[];
      bookingSource: 'online' | 'offline'; offlinePaymentMode: 'self_collected' | 'payment_link' | null;
      event: { title: string; slug: string; date: string; durationHrs: number; venueName: string | null; city: string | null; organizerBrandName: string | null };
    }>(`/bookings/ticket-view/${encodeURIComponent(token)}`),
  // Public, no auth, same qrToken-as-credential model as ticketView — the
  // real server-generated PDF (identical to what's emailed at booking
  // time), not a JSON response, so this triggers the browser download
  // directly rather than going through apiFetch. Every "Download ticket"
  // button in the app calls this now (2026-10-14), replacing the separate
  // client-side canvas/PNG lib/ticket.ts used to generate.
  ticketPdf: async (token: string) => {
    const res = await fetch(`${API_URL}/bookings/ticket-pdf/${encodeURIComponent(token)}`);
    if (!res.ok) throw new ApiError(res.status, 'DOWNLOAD_FAILED', 'Could not download your ticket');
    // Real filename comes from the server's Content-Disposition
    // (prebooze-ticket-<booking id>.pdf) — once the response becomes a
    // blob that header is gone, so it has to be read here before that,
    // not re-derived from the token.
    const disposition = res.headers.get('content-disposition') ?? '';
    const match = /filename="([^"]+)"/.exec(disposition);
    const filename = match?.[1] ?? 'prebooze-ticket.pdf';
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  },
};

// ---------- wallet / payments ----------
export const wallet = {
  balance: () => apiFetch<{ balance: number; txs: WalletTx[] }>('/wallet'),
  payMethods: () => apiFetch<PayMethod[]>('/pay-methods'),
  addPayMethod: (m: Omit<PayMethod, 'id' | 'isDefault'> & { cvvToken?: string }) => apiFetch<PayMethod>('/pay-methods', { body: m }),
  removePayMethod: (id: string) => apiFetch<void>(`/pay-methods/${id}`, { method: 'DELETE' }),
  setDefault: (id: string) => apiFetch<void>(`/pay-methods/${id}/default`, { method: 'POST' }),
  setAutoRenew: (on: boolean) => apiFetch<void>('/me/auto-renew', { body: { on } }),
};

// ---------- referrals ----------
export const referrals = {
  mine: () => apiFetch<{ code: string; referrals: Referral[] }>('/referrals'),
  claim: (code: string) => apiFetch<void>('/referrals/claim', { body: { code } }),
};

// ---------- social ----------
export const social = {
  follow: (key: string) => apiFetch<void>('/follows', { body: { key } }),
  unfollow: (key: string) => apiFetch<void>('/follows', { method: 'DELETE', body: { key } }),
  mySocialState: () => apiFetch<{ following: string[]; interested: string[]; wishlist: string[]; favouriteVenues: string[] }>('/me/social'),
  followers: () => apiFetch<Person[]>('/me/followers'),
  setAttendanceVisibility: (v: 'off' | 'followers' | 'public') => apiFetch<void>('/me/attendance-visibility', { body: { v } }),
  setDiscoverable: (on: boolean) => apiFetch<void>('/me/discoverable', { body: { on } }),
  setMarketingConsent: (on: boolean) => apiFetch<void>('/me/marketing-consent', { body: { on } }),
  interested: (eventId: string, on: boolean) => apiFetch<void>(`/events/${eventId}/interested`, { body: { on } }),
  wishlist: (eventId: string, on: boolean) => apiFetch<void>(`/events/${eventId}/wishlist`, { body: { on } }),
  favVenue: (venueId: string, on: boolean) => apiFetch<void>(`/venues/${venueId}/favourite`, { body: { on } }),
  reviewOrganizer: (orgId: string, rating: number, text: string) => apiFetch<GuestReview>(`/organizers/${orgId}/reviews`, { body: { rating, text } }),
  organizerReviews: (orgId: string) => apiFetch<GuestReview[]>(`/organizers/${orgId}/reviews`),
};

// ---------- subscriptions (organizer / promoter / venue / lineup billing) ----------
// One real, Razorpay-backed billing surface shared by all four roles — see
// SubscriptionsService on the backend. Paid tiers return `shortUrl`, a hosted
// Razorpay authorization link the caller must redirect the owner to; nothing
// actually activates until the `subscription.activated` webhook fires.
export interface SubTier {
  id: string;
  role: 'organizer' | 'promoter' | 'venue' | 'lineup';
  name: string;
  price: number;
  guests: number | null; // promoter-only monthly guest-list quota, -1 = unlimited
}
export interface SubscriptionCharge {
  id: string;
  amount: number;
  status: 'captured' | 'failed';
  occurredAt: string;
}
export interface RoleSubscription {
  id: string;
  role: SubTier['role'];
  entityId: string;
  tierId: string;
  tier: SubTier;
  status: 'created' | 'authenticated' | 'active' | 'pending' | 'halted' | 'cancelled' | 'completed' | 'expired';
  shortUrl: string | null;
  currentStart: string | null;
  currentEnd: string | null;
  paidCount: number;
  charges?: SubscriptionCharge[];
}
export interface SubscribeResult {
  ok: boolean;
  requiresAuthorization: boolean;
  subscriptionId?: string;
  shortUrl?: string;
  keyId?: string;
}
function subscriptionApi(prefix: 'organizer' | 'promoter' | 'venue' | 'lineup') {
  return {
    tiers: () => apiFetch<SubTier[]>(`/${prefix}/subscription/tiers`),
    mine: () => apiFetch<RoleSubscription | null>(`/${prefix}/subscription`),
    subscribe: (tierId: string) => apiFetch<SubscribeResult>(`/${prefix}/subscription`, { body: { tierId } }),
    cancel: () => apiFetch<{ ok: boolean }>(`/${prefix}/subscription/cancel`, { method: 'POST' }),
  };
}

// ---------- promoter ----------
export interface PromoterMe {
  id: string;
  slug: string;
  name: string;
  city: string;
  country: string | null;
  state: string | null;
  pincode: string | null;
  bio: string;
  links: string[];
  verified: boolean;
  followers: number;
  eventsPromoted: number;
  guestsBrought: number;
  planId: string;
  audienceReach: string | null;
  bankName: string | null;
  bankLast4: string | null;
  accountHolderName: string | null;
  ifsc: string | null;
  logoUrl: string | null;
  createdAt: string;
}
export interface PromoterTeamMember extends SubPromoter {
  id: string;
}
export interface PromoterWithdrawal {
  id: string;
  amount: number;
  createdAt: string;
}
export interface LeaderboardRow {
  id: string;
  slug: string;
  name: string;
  verified: boolean;
  followers: number;
  brought: number;
  rate: number | null;
}
export interface PromoterPass extends PromoterGuest {
  event: Event;
}
export const promoter = {
  me: () => apiFetch<PromoterMe>('/promoter/me'),
  updateMe: (patch: {
    brandName?: string; username?: string; city?: string; country?: string; state?: string; pincode?: string;
    bio?: string; links?: string[]; audienceReach?: string;
    bankName?: string; bankAccount?: string; accountHolderName?: string; ifsc?: string; logoUrl?: string;
  }) => apiFetch<PromoterMe>('/promoter/me', { method: 'PATCH', body: patch }),
  upload: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return apiUpload<{ url: string }>('/promoter/upload', form);
  },
  invoices: () => apiFetch<Invoice[]>('/promoter/invoices'),
  downloadInvoicePdf: async (id: string, filename: string) => {
    const res = await fetch(`${API_URL}/promoter/invoices/${id}/pdf`, { headers: getToken() ? { Authorization: `Bearer ${getToken()}` } : {} });
    if (!res.ok) throw new ApiError(res.status, 'ERROR', 'Failed to download PDF');
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  },
  promotions: () => apiFetch<Event[]>('/promoter/promotions'),
  // Prebooze's own promoter-referral program (2026-09-02) — every live
  // event in the promoter's city, independent of promotions() above (which
  // only lists organizer-invited events). Generate a ?ref= link for any of
  // these; a paid booking through it earns 2% of Prebooze's own commission,
  // no organizer opt-in needed.
  cityEvents: () =>
    apiFetch<
      { id: string; slug: string; title: string; posterUrl: string | null; date: string; durationHrs: number; city: string; organizerName: string | null; minPrice: number }[]
    >('/promoter/city-events'),
  platformEarnings: () => apiFetch<{ total: number; paidOut: number; pending: number }>('/promoter/platform-earnings'),
  guests: (eventId: string) => apiFetch<PromoterGuest[]>(`/promoter/events/${eventId}/guests`),
  paidGuests: (eventId: string) =>
    apiFetch<{ id: string; mainGuest: string; qty: number; subtotal: number; promoterCommission: number; createdAt: string }[]>(
      `/promoter/events/${eventId}/paid-guests`
    ),
  captureGuest: (eventSlug: string, promoterSlug: string, guest: Omit<PromoterGuest, 'id' | 'createdAt' | 'arrived'>) =>
    apiFetch<PromoterGuest>(`/p/${eventSlug}/${promoterSlug}`, { body: guest }),
  pass: (id: string) => apiFetch<PromoterPass>(`/p/pass/${id}`),
  checkInGuest: (id: string) => apiFetch<void>(`/promoter/guests/${id}/check-in`, { method: 'POST' }),
  earnings: () => apiFetch<{ perHead: number; commission: number; withdrawn: number }>('/promoter/earnings'),
  eventEarnings: () =>
    apiFetch<
      {
        eventId: string; title: string; date: string; organizerId: string; organizerName: string;
        perHead: number; perHeadRate: number; perHeadCount: number; commission: number; total: number;
        status: 'pending' | 'reminder_sent' | 'received';
      }[]
    >('/promoter/events/earnings'),
  markEventReceived: (eventId: string) => apiFetch<{ ok: true }>(`/promoter/events/${eventId}/mark-received`, { method: 'POST' }),
  remindOrganizerToPay: (eventId: string) => apiFetch<{ ok: true }>(`/promoter/events/${eventId}/remind-payout`, { method: 'POST' }),
  withdraw: (amount: number) => apiFetch<void>('/promoter/withdraw', { body: { amount } }),
  withdrawals: () => apiFetch<PromoterWithdrawal[]>('/promoter/withdrawals'),
  team: () => apiFetch<PromoterTeamMember[]>('/promoter/team'),
  addTeamMember: (m: SubPromoter) => apiFetch<PromoterTeamMember>('/promoter/team', { body: m }),
  updateTeamMember: (id: string, patch: { payoutSplitPct?: number; monthlyQuotaShare?: number | null }) =>
    apiFetch<PromoterTeamMember>(`/promoter/team/${id}`, { method: 'PATCH', body: patch }),
  removeTeamMember: (id: string) => apiFetch<{ ok: true }>(`/promoter/team/${id}`, { method: 'DELETE' }),
  teamEarnings: () =>
    apiFetch<
      {
        teamMemberId: string; memberName: string; eventId: string; eventTitle: string; eventDate: string;
        perHead: number; commission: number; rawTotal: number; splitPct: number; owed: number; status: 'pending' | 'paid';
      }[]
    >('/promoter/team/earnings'),
  markTeamMemberPaid: (teamMemberId: string, eventId: string) =>
    apiFetch<{ ok: true }>(`/promoter/team/${teamMemberId}/events/${eventId}/mark-paid`, { method: 'POST' }),
  leaderboard: () => apiFetch<LeaderboardRow[]>('/promoter/leaderboard'),
  usage: () => apiFetch<{ used: number; quota: number }>('/promoter/usage'),
  subscription: subscriptionApi('promoter'),
};

// One in-app notification for an organizer — same shape prebooze-organizer's
// RN app already renders (NotificationsScreen.tsx), just ported to web.
export interface OrgNotification {
  id: string;
  icon: string;
  text: string;
  to: string | null;
  read: boolean;
  createdAt: string;
}

// Booking-level row across every event — one per booking, not per guest
// (see OrgAttendee below, which stays per-guest for the scanner). Same
// shape organizer/venue's Bookings page and admin's own Bookings page use.
export interface OrgBooking {
  id: string;
  mainGuest: string;
  whatsapp: string;
  tierName: string;
  qty: number;
  total: number;
  status: 'confirmed' | 'cancelled' | 'refunded' | 'refund_requested';
  checkedIn: boolean;
  createdAt: string;
  bookingSource: 'online' | 'offline';
  offlinePaymentMode: 'self_collected' | 'payment_link' | null;
  event: { id: string; title: string; date: string; durationHrs: number };
}

// Full single-booking detail — organizer-scoped equivalent of admin's
// LiveBooking, minus admin-only fields (adminNote) and minus every admin
// mutation action (refund approve/decline/retry stay admin-only; an
// organizer can see refund state here but not act on it).
export interface OrgBookingDetail {
  id: string;
  mainGuest: string;
  whatsapp: string;
  tierName: string;
  qty: number;
  subtotal: number;
  fee: number;
  discount: number;
  total: number;
  coverCharge: number;
  couponCode?: string | null;
  // From the guest's own Invoice record — see OrganizerService.bookingDetail's
  // own doc comment for why this isn't just a plain field on the booking.
  gst?: { gstPct: number; gstAmount: number; igstAmount: number } | null;
  status: 'confirmed' | 'cancelled' | 'refunded' | 'refund_requested';
  guests: { name: string; checkedIn: boolean; gender?: string; whatsapp?: string }[];
  walletCreditUsed: number;
  paymentId?: string | null;
  paymentMethod?: string | null;
  bookingSource: 'online' | 'offline';
  offlinePaymentMode?: 'self_collected' | 'payment_link' | null;
  refundedTo?: string | null;
  refundFailedAt?: string | null;
  refundGatewayState?: string | null;
  refundGatewayRefundId?: string | null;
  refundGatewayAmount?: number | null;
  orgApprovedRefundAt?: string | null;
  qrToken: string;
  checkedIn: boolean;
  checkedInAt?: string | null;
  createdAt: string;
  promoterRef?: string | null;
  promoterCommission: number;
  promoter?: { id: string; name: string; slug: string } | null;
  // Which staff/owner account created this offline booking — null for
  // every ordinary online booking.
  createdBy?: { name: string | null; phone: string } | null;
  user: { name: string; phone: string; email?: string };
  event: { id: string; title: string; date: string; durationHrs: number; venue?: { name: string; city: string } | null; organizer?: { brandName: string } | null };
}

// ---------- organizer ----------
export interface OrgAttendee {
  bookingId: string;
  bookingStatus: string;
  tierName: string;
  name: string;
  isMainGuest: boolean;
  // Only ever set for the main guest (the real buyer account) — +1s are
  // free-text names with no account, so they never link anywhere.
  username?: string;
  avatarUrl?: string;
  gender?: string;
  whatsapp: string;
  checkedIn: boolean;
  coverCharge: number;
  total: number;
  // The tier's own price at purchase, unaffected by a coupon/wallet credit
  // that later zeroed out `total` — the scanner uses this (not total) to
  // tell a genuinely free tier apart from a paid tier discounted to ₹0.
  subtotal: number;
  promoterName?: string;
  paymentMethod: string;
  bookingSource: 'online' | 'offline';
  offlinePaymentMode: 'self_collected' | 'payment_link' | null;
}
export interface OrgLedgerTx {
  id: string;
  // commission_adjustment/commission_gst_adjustment/tcs_adjustment: a
  // one-time retroactive correction (2026-10-02/03/04) for a sale credited
  // before the commission, its GST, or TCS was actually turned on for the
  // event — real money, same as a 'sale' row, just applied after the fact.
  // See `note` for why each one happened.
  type: 'sale' | 'refund' | 'withdrawal' | 'withdrawal_reversal' | 'commission_adjustment' | 'commission_gst_adjustment' | 'tcs_adjustment';
  amount: number;
  eventId?: string;
  eventTitle?: string;
  note?: string;
  createdAt: string;
  // Real request→received→initiated→processed→complete pipeline (or
  // rejected), 2026-09-18 — only meaningful for type:'withdrawal' rows.
  withdrawalStatus?: 'requested' | 'received' | 'initiated' | 'processed' | 'complete' | 'rejected';
  withdrawalRejectedReason?: string;
  withdrawalPaidUtr?: string;
  // Only present for a row with a real Booking behind it — see
  // OrganizerService.payouts().
  bookingBreakup?: {
    subtotal: number; fee: number; gstAmount: number; commission: number; includesFeeAndGst: boolean;
    commissionGstAmount: number; tcsAmount: number;
  };
}
/** Shared client for the organizer/venue-paid Meta ad marketing endpoints —
 * identical shape on both sides (see MarketingController/
 * VenueMarketingController), only the route base differs. */
const marketingApi = (base: string) => ({
  rates: () => apiFetch<MarketingRates>(`${base}/rates`),
  request: (eventId: string) =>
    apiFetch<{ id: string; amount: number; phonepeRedirectUrl: string }>(`${base}/request`, { body: { eventId } }),
  // A one-time purchase covering every event run over the chosen window
  // (7/15/30 days) — NOT a recurring subscription/mandate (no PhonePe
  // AutoPay yet). Same request/confirm/abandon flow as a per-event purchase.
  requestPeriod: (days: 7 | 15 | 30) =>
    apiFetch<{ id: string; amount: number; phonepeRedirectUrl: string }>(`${base}/request-period`, { body: { days } }),
  // No proof body — PhonePe's own getOrderStatus is the sole source of
  // truth server-side, same as guest ticket checkout.
  confirmPayment: (id: string) =>
    apiFetch<MarketingOrder>(`${base}/${id}/confirm-payment`, { body: {} }),
  // Called once the resume loop gives up on an unconfirmed payment — frees
  // the event up for a fresh purchase attempt immediately, instead of
  // leaving it stuck until requestForEvent()'s own 15-minute staleness
  // fallback kicks in.
  abandon: (id: string) => apiFetch<{ deleted: boolean }>(`${base}/${id}/abandon`, { body: {} }),
  orders: () => apiFetch<MarketingOrder[]>(`${base}/orders`),
  subscribe: () => apiFetch<{ ok: boolean; requiresAuthorization: boolean; shortUrl?: string; subscriptionId?: string; keyId?: string }>(`${base}/subscribe`, { body: {} }),
  cancelSubscription: () => apiFetch<{ ok: boolean }>(`${base}/subscription/cancel`, { body: {} }),
  mySubscription: () => apiFetch<MarketingSubscription | null>(`${base}/subscription`),
  analytics: (eventId: string) => apiFetch<MarketingAnalytics>(`${base}/analytics`, { query: { eventId } }),
  analyticsPeriod: (orderId: string) => apiFetch<MarketingPeriodAnalytics>(`${base}/analytics-period`, { query: { orderId } }),
  // ---------- hero banner / reel marketing ----------
  placementRates: (productType: 'banner' | 'reel') => apiFetch<PlacementRates>(`${base}/placement-rates/${productType}`),
  requestPlacement: (productType: 'banner' | 'reel', eventId: string, days: 3 | 7 | 15 | 30, creativeUrl: string, creativePosterUrl?: string) =>
    apiFetch<{ id: string; amount: number; phonepeRedirectUrl: string }>(`${base}/request-placement`, { body: { productType, eventId, days, creativeUrl, creativePosterUrl } }),
});

export const organizer = {
  notifications: () => apiFetch<OrgNotification[]>('/organizer/notifications'),
  unreadNotificationCount: () => apiFetch<{ count: number }>('/organizer/notifications/unread-count'),
  markNotificationRead: (id: string) => apiFetch<{ ok: true }>(`/organizer/notifications/${encodeURIComponent(id)}/read`, { method: 'POST' }),
  markAllNotificationsRead: () => apiFetch<{ ok: true }>('/organizer/notifications/read-all', { method: 'POST' }),
  me: () => apiFetch<Organizer>('/organizer/me'),
  updateMe: (patch: { brandName?: string; username?: string; city?: string; country?: string; state?: string; pincode?: string; logoUrl?: string; about?: string; socialLinks?: { instagram?: string; facebook?: string; other?: string[] }; contact?: string; contactPerson?: string; phone?: string; eventTypes?: string }) =>
    apiFetch<Organizer>('/organizer/me', { method: 'PATCH', body: patch }),
  upload: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return apiUpload<{ url: string; posterUrl?: string }>('/organizer/upload', form);
  },
  invoices: () => apiFetch<Invoice[]>('/organizer/invoices'),
  downloadInvoicePdf: async (id: string, filename: string) => {
    const res = await fetch(`${API_URL}/organizer/invoices/${id}/pdf`, { headers: getToken() ? { Authorization: `Bearer ${getToken()}` } : {} });
    if (!res.ok) throw new ApiError(res.status, 'ERROR', 'Failed to download PDF');
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  },
  events: () => apiFetch<Event[]>('/organizer/events'),
  upsertEvent: (e: {
    id?: string; title: string; description?: string; category?: string; subCategory?: string; ageLimit?: string;
    tags?: string[]; date?: string; durationHrs?: number; venueId?: string; privateCity?: string; privateLocality?: string;
    exactAddress?: string | null; mapLink?: string | null; status?: 'draft' | 'pending';
    unlistedVenueName?: string; unlistedVenueCity?: string; unlistedVenueAddress?: string; unlistedVenueInstagramUrl?: string;
    conditions?: string[]; rules?: unknown; lineup?: unknown; seo?: unknown; promoterConfig?: unknown;
    posterUrl?: string | null; galleryUrls?: string[]; teaserVideoUrl?: string | null; socialBanners?: { postUrl?: string; storyUrl?: string };
    tiers?: { id?: string; name: string; price: number; quantity: number; includes?: string[]; description?: string }[];
    collaboratorOrganizerIds?: string[];
    freeTextCollaborators?: { name: string; instagramUrl?: string }[];
  }) => apiFetch<Event>('/organizer/events', { body: e }),
  // Real delete, not a status change — blocked server-side the instant a
  // single real Booking exists (see OrganizerService.deleteEvent).
  deleteEvent: (id: string) => apiFetch<{ ok: true }>(`/organizer/events/${id}`, { method: 'DELETE' }),
  // Manual resend of the private-address location WhatsApp — see
  // OrganizerService.resendEventLocation. Not gated on the scheduled
  // ~3h-before send already having fired.
  resendEventLocation: (id: string) => apiFetch<{ sent: number }>(`/organizer/events/${id}/resend-location`, { method: 'POST' }),
  // Real, registered organizers this organizer can tag as a co-organizer —
  // not gated on `verified`, unlike venuePartner.collaboratorOptions below
  // (that one's a lighter, read-only credit; this one grants full access,
  // but the bar decided for it was "registered", not "verified").
  collaboratorOptions: () => apiFetch<VenueCollaboratorOption[]>('/organizer/collaborator-options'),
  attendees: (eventId: string) => apiFetch<OrgAttendee[]>(`/organizer/events/${eventId}/attendees`),
  bookings: () => apiFetch<OrgBooking[]>('/organizer/bookings'),
  bookingDetail: (id: string) => apiFetch<OrgBookingDetail>(`/organizer/bookings/${encodeURIComponent(id)}`),
  // Walk-up/phone/gate-inquiry bookings created directly from the console —
  // see BookingsService.createOfflineBookingSelfCollected/PaymentLink.
  // 'self_collected' returns the real created Booking immediately;
  // 'payment_link' returns a redirect URL to text the guest (the booking
  // itself is only created once they actually pay, via the same webhook
  // every online checkout already uses).
  createOfflineBooking: (body: {
    eventId: string; lines: { tierId: string; qty: number }[]; guestName: string; whatsapp: string; gender?: string;
    others?: { name: string; gender?: string; whatsapp?: string }[];
    paymentMode: 'self_collected' | 'payment_link';
  }) => apiFetch<OrgBooking | { holdId: string; redirectUrl: string; subtotal: number; fee: number; gstPct: number; gst: number; total: number; phone: string }>('/organizer/offline-bookings', { body }),
  offlineCharges: () => apiFetch<{ id: string; bookingId: string | null; eventTitle: string | null; guestName: string | null; guestPaid: number | null; commissionCharged: number; createdAt: string }[]>('/organizer/offline-charges'),
  // Organizer's own "undo" for a self-collected offline booking — reverses
  // inventory + the commission/fee/GST ledger debit. Payment-link bookings
  // aren't voidable here (real gateway money — use the normal refund path).
  voidOfflineBooking: (id: string) => apiFetch<OrgBooking>(`/organizer/offline-bookings/${encodeURIComponent(id)}/void`, { method: 'POST' }),
  resendOfflineBooking: (id: string) => apiFetch<{ ok: true }>(`/organizer/offline-bookings/${encodeURIComponent(id)}/resend`, { method: 'POST' }),
  // Organizer's own sign-off on a pending refund request — does not move
  // any money, just tells admin the organizer already agrees. Real
  // approve/decline stays admin-only.
  orgApproveRefund: (id: string) => apiFetch<OrgBookingDetail>(`/organizer/bookings/${encodeURIComponent(id)}/refund/org-approve`, { method: 'POST' }),
  // Deliberately its own endpoint rather than events() above — gated on
  // 'Attendees & check-in' view (same as the create call), not 'Events &
  // wizard', so staff who can only take bookings can still use this modal.
  offlineBookingEvents: () => apiFetch<Event[]>('/organizer/offline-bookings/events'),
  marketing: marketingApi('/organizer/marketing'),
  coupons: () => apiFetch<Coupon[]>('/organizer/coupons'),
  upsertCoupon: (c: Partial<Coupon>) => apiFetch<Coupon>('/organizer/coupons', { body: c }),
  payouts: () => apiFetch<{ balance: number; ledger: OrgLedgerTx[] }>('/organizer/payouts'),
  // Real confirmed-Booking-row counts + online/offline revenue split — see
  // OrganizerService.bookingStats. The Dashboard's own "Total bookings"
  // used to derive from ledger 'sale' entries instead, which silently
  // undercounts self-collected offline bookings (no 'sale' entry posted
  // for those) — this is the same real-row definition admin uses.
  bookingStats: () => apiFetch<{ totalBookings: number; totalCustomers: number; revenueOnline: number; revenueOffline: number; revenueTotal: number }>('/organizer/booking-stats'),
  promoterPayouts: () =>
    apiFetch<{ eventId: string; eventTitle: string; eventDate: string; promoterId: string; promoterName: string; perHead: number; commission: number; total: number; status: 'pending' | 'reminder_sent' | 'received' }[]>(
      '/organizer/promoter-payouts'
    ),
  promoters: () =>
    apiFetch<
      {
        promoterId: string; promoterSlug: string; promoterName: string; city: string; bio: string; contact: string | null; verified: boolean;
        bankName: string | null; bankAccountNumber: string | null; bankLast4: string | null; accountHolderName: string | null; ifsc: string | null;
        eventCount: number; totalOwed: number; pendingEvents: number;
      }[]
    >('/organizer/promoters'),
  withdraw: (amount: number) => apiFetch<void>('/organizer/withdraw', { body: { amount } }),
  // ---- payment profiles (bank accounts to withdraw to) — self-serve,
  // no admin review, plural. Entirely separate from identity verification
  // (kyc.submitOrganizerVerification above). ----
  paymentProfiles: () => apiFetch<PaymentProfile[]>('/organizer/payment-profiles'),
  createPaymentProfile: (data: {
    legalName: string; businessAddress: string; country?: string; state?: string; city?: string; pincode?: string;
    bankAccountNumber: string; accountHolderName: string; ifsc: string; branch?: string;
    pan: string; gstin?: string; noGst?: boolean;
  }) => apiFetch<PaymentProfile>('/organizer/payment-profiles', { body: data }),
  updatePaymentProfile: (id: string, data: Partial<{
    legalName: string; businessAddress: string; country: string; state: string; city: string; pincode: string;
    bankAccountNumber: string; accountHolderName: string; ifsc: string; branch: string;
    pan: string; gstin: string; noGst: boolean;
  }>) => apiFetch<PaymentProfile>(`/organizer/payment-profiles/${id}`, { method: 'PATCH', body: data }),
  deletePaymentProfile: (id: string) => apiFetch<{ ok: true }>(`/organizer/payment-profiles/${id}`, { method: 'DELETE' }),
  setDefaultPaymentProfile: (id: string) => apiFetch<{ ok: true }>(`/organizer/payment-profiles/${id}/default`, { method: 'POST' }),
  abandonedCarts: () => apiFetch<CartRecord[]>('/organizer/carts'),
  remindCart: (id: string) => apiFetch<void>(`/organizer/carts/${id}/remind`, { method: 'POST' }),
  // ---- gate ops: guest list, promoter guests, live monitor ----
  guestList: (eventId: string) => apiFetch<{ entries: OrgGuestListEntry[]; namesCount: number; totalHeads: number; arrived: number }>(`/organizer/events/${eventId}/guest-list`),
  addGuestListEntry: (eventId: string, body: { name: string; phone: string; plusOnes?: number; companions?: { name: string; phone: string }[] }) =>
    apiFetch<OrgGuestListEntry>(`/organizer/events/${eventId}/guest-list`, { body }),
  toggleGuestArrived: (id: string) => apiFetch<OrgGuestListEntry>(`/organizer/guest-list/${id}/toggle-arrived`, { method: 'POST' }),
  removeGuestListEntry: (id: string) => apiFetch<{ ok: true }>(`/organizer/guest-list/${id}`, { method: 'DELETE' }),
  promoterGuests: (eventId: string) => apiFetch<OrgPromoterGuest[]>(`/organizer/events/${eventId}/promoter-guests`),
  live: (eventId: string) => apiFetch<OrgLiveMonitor>(`/organizer/events/${eventId}/live`),
  manualCheckIn: (eventId: string, name: string, count?: number) => apiFetch<unknown>(`/organizer/events/${eventId}/check-in`, { body: { name, count } }),
  setSalesPaused: (eventId: string, paused: boolean) => apiFetch<Event>(`/organizer/events/${eventId}/pause-sales`, { method: 'PATCH', body: { paused } }),
};

export interface OrgGuestListEntry {
  id: string;
  eventId: string;
  name: string;
  phone: string;
  plusOnes: number;
  companions: { name: string; phone: string }[];
  addedBy: string;
  arrived: boolean;
  createdAt: string;
}
export interface VipPass extends OrgGuestListEntry {
  event: Event;
}
export const vip = {
  pass: (id: string) => apiFetch<VipPass>(`/vip/pass/${id}`),
};
export interface OrgPromoterGuest {
  id: string;
  eventId: string;
  promoterSlug: string;
  promoterName?: string; // resolved real brand name — promoterSlug is the URL-style slug, not necessarily readable
  name: string;
  phone: string;
  age?: string;
  gender?: string;
  arrived: boolean;
  arrivedAt?: string;
  createdAt: string;
}
export interface OrgLiveMonitor {
  total: number;
  checkedIn: number;
  remaining: number;
  pct: number;
  scanRate: number;
  rejected: number;
  histogram: number[];
  feed: { ok: boolean; text: string; at: string }[];
  salesPaused: boolean;
}

// ---------- organizer team & roles ----------
export interface OrgStaffMember {
  id: string;
  name: string;
  phone: string | null;
  roleName: string;
  scan: boolean;
  createdAt: string;
}
export type OrgPermKey = 'view' | 'edit';
export type OrgModulePerms = Record<string, Record<OrgPermKey, boolean>>; // module -> {view, edit}
export interface OrgTeamAccess {
  organizerId: string;
  organizerBrand: string;
  organizerLogoUrl: string | null;
  roleName: string;
  permissions: OrgModulePerms;
  scan: boolean;
}
export const orgTeam = {
  /** "Am I on someone's team?" — null if not. Called once after login to
   * decide whether to route a non-owner into a permission-scoped organizer
   * console. */
  mine: () => apiFetch<OrgTeamAccess | null>('/organizer/team/mine'),
  listStaff: () => apiFetch<OrgStaffMember[]>('/organizer/team'),
  addStaff: (body: { name: string; phone: string; email?: string; roleName?: string; scan?: boolean }) => apiFetch<OrgStaffMember>('/organizer/team', { body }),
  updateStaffRole: (id: string, roleName: string) => apiFetch<OrgStaffMember>(`/organizer/team/${id}/role`, { body: { roleName } }),
  removeStaff: (id: string) => apiFetch<{ ok: true }>(`/organizer/team/${id}`, { method: 'DELETE' }),
};
export const orgRoles = {
  list: () => apiFetch<Record<string, OrgModulePerms>>('/organizer/roles'), // roleName -> module -> {view, edit}
  add: (name: string) => apiFetch<unknown>('/organizer/roles', { body: { name } }),
  setPerm: (name: string, module: string, key: OrgPermKey, value: boolean) =>
    apiFetch<unknown>(`/organizer/roles/${encodeURIComponent(name)}/perm`, { body: { module, key, value } }),
  remove: (name: string) => apiFetch<{ ok: true }>(`/organizer/roles/${encodeURIComponent(name)}`, { method: 'DELETE' }),
};

// ---------- lineup (artist) ----------
export const lineup = {
  me: () => apiFetch<LineupProfile>('/lineup/me'),
  upload: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return apiUpload<{ url: string }>('/lineup/upload', form);
  },
  updateMe: (patch: { name?: string; category?: string; city?: string; state?: string; country?: string; pincode?: string; bio?: string; links?: string[]; logoUrl?: string; username?: string }) =>
    apiFetch<LineupProfile>('/lineup/me', { method: 'PATCH', body: patch }),
  events: () => apiFetch<(Event & { myRole?: string })[]>('/lineup/events'),
  invoices: () => apiFetch<Invoice[]>('/lineup/invoices'),
  downloadInvoicePdf: async (id: string, filename: string) => {
    const res = await fetch(`${API_URL}/lineup/invoices/${id}/pdf`, { headers: getToken() ? { Authorization: `Bearer ${getToken()}` } : {} });
    if (!res.ok) throw new ApiError(res.status, 'ERROR', 'Failed to download PDF');
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  },
  subscription: subscriptionApi('lineup'),
};

// ---------- featured ----------
export const featured = {
  request: (input: { type: Featured['type']; refId: string; billing: 'per_event' | 'monthly' }) =>
    apiFetch<Featured & { phonepeRedirectUrl: string }>('/featured/request', { body: input }),
  // No proof body anymore — PhonePe's own getOrderStatus is the sole source
  // of truth server-side, same as guest ticket checkout.
  confirmPayment: (id: string) =>
    apiFetch<Featured>(`/featured/${id}/confirm-payment`, { body: {} }),
  mine: (type: Featured['type'], refId: string) => apiFetch<Featured | null>('/featured/mine', { query: { type, refId } }),
  rates: () => apiFetch<{ perEvent: number; organizerMonthly: number; promoterMonthly: number; lineupMonthly: number; venueMonthly: number }>('/featured/rates'),
  // Real Razorpay Subscription (e-mandate) — auto-renews monthly, no manual
  // "renew now" click needed. organizer/promoter/lineup/venue only.
  subscribe: (input: { type: FeaturedSubscription['type']; refId: string }) =>
    apiFetch<{ ok: boolean; requiresAuthorization: boolean; shortUrl?: string; subscriptionId?: string; keyId?: string }>('/featured/subscribe', { body: input }),
  cancelSubscription: (type: FeaturedSubscription['type'], refId: string) =>
    apiFetch<{ ok: boolean }>('/featured/subscription/cancel', { body: { type, refId } }),
  mySubscription: (type: FeaturedSubscription['type'], refId: string) =>
    apiFetch<FeaturedSubscription | null>('/featured/mine-subscription', { query: { type, refId } }),
};

// ---------- support / careers / misc ----------
export const support = {
  tickets: () => apiFetch<HelpTicket[]>('/support/tickets'),
  raise: (t: Omit<HelpTicket, 'id' | 'status' | 'createdAt'>) => apiFetch<HelpTicket>('/support/tickets', { body: t }),
  ticket: (id: string) => apiFetch<HelpTicket>(`/support/tickets/${id}`),
  reply: (id: string, message: string) => apiFetch<HelpTicketReply>(`/support/tickets/${id}/reply`, { body: { message } }),
  // Public Contact-us form — no auth, unlike the ticket endpoints above.
  contact: (body: { name: string; email: string; role: string; message: string }) =>
    apiFetch<{ id: string }>('/support/contact', { body }),
};

export const careers = {
  jobs: () => apiFetch<CareerJob[]>('/careers/jobs'),
  // multipart, not JSON — the optional cv file rides in the same request as
  // the application itself rather than through a separate freestanding
  // public upload endpoint (this app has no anonymous/unauthenticated
  // upload route anywhere else; bundling it here keeps that true).
  apply: (a: Omit<JobApplication, 'id' | 'appliedAt' | 'cv'>, cv?: File) => {
    const form = new FormData();
    Object.entries(a).forEach(([k, v]) => form.append(k, v));
    if (cv) form.append('cv', cv);
    return apiUpload<JobApplication>('/careers/apply', form);
  },
};

export const notifications = {
  send: (channel: 'whatsapp' | 'email', to: string, template: string, data: Record<string, string>) =>
    apiFetch<void>('/notifications/send', { body: { channel, to, template, data } }),
};

// ---------- platform settings (public, no auth) ----------
export interface PlatformInfo {
  maintenanceMode: boolean;
  comingSoonMode: boolean;
  salesPaused: boolean;
  socials: { instagram: string; x: string; youtube: string; whatsapp: string; facebook: string };
  siteSeo: { title: string; description: string; keywords: string };
  contact: { email: string; phone: string; address: string; organizerEmail: string };
  footerCopyright: string;
  feeLabel: string;
  absorbedBy: 'Organizer' | 'Guest' | 'Split' | string;
  bookingFee: number;
  // 0 whenever PlatformSettings.gstEnabled is false — real GSTIN activated
  // 2026-09-21. Charged on the booking fee only for a guest ticket
  // purchase (see BookingsService.priceHold's own doc comment).
  gstPct: number;
  logoUrl: string | null;
  faviconUrl: string | null;
}
export const platform = {
  settings: () => apiFetch<PlatformInfo>('/settings'),
};

export interface VenueLedgerTx {
  id: string;
  type: 'sale' | 'refund' | 'withdrawal' | 'withdrawal_reversal' | 'commission_adjustment' | 'commission_gst_adjustment' | 'tcs_adjustment';
  amount: number;
  eventId?: string;
  eventTitle?: string;
  note?: string;
  createdAt: string;
  // Same real pipeline as OrgLedgerTx's identical fields (2026-09-18) —
  // only meaningful for type:'withdrawal' rows.
  withdrawalStatus?: 'requested' | 'received' | 'initiated' | 'processed' | 'complete' | 'rejected';
  withdrawalRejectedReason?: string;
  withdrawalPaidUtr?: string;
  // Real GST (on Prebooze's commission) + TCS withheld on this 'sale', in
  // rupees — see VenueLedgerTx's schema comment. Both 0 when off.
  commissionGstAmount?: number;
  tcsAmount?: number;
}
export interface VenueHostingRequest {
  id: string;
  status: 'pending' | 'approved' | 'rejected';
  contactedAt: string | null;
  reviewNote: string | null;
  reviewedAt: string | null;
  createdAt: string;
}
export interface VenueCollaboratorOption {
  id: string;
  brandName: string;
  username: string;
  city: string;
}
export interface VenuePaymentProfile {
  id: string;
  venueId: string;
  isDefault: boolean;
  legalName: string;
  businessAddress: string;
  country?: string | null;
  state?: string | null;
  city?: string | null;
  pincode?: string | null;
  bankAccountNumber: string;
  bankLast4: string;
  accountHolderName: string;
  ifsc: string;
  branch?: string | null;
  pan: string;
  gstin?: string | null;
  noGst: boolean;
  createdAt: string;
  updatedAt: string;
}

// ---------- venue partner ----------
export const venuePartner = {
  upload: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return apiUpload<{ url: string; posterUrl?: string }>('/venue/upload', form);
  },
  onboard: (v: Partial<Venue>) => apiFetch<Venue>('/venue/onboard', { body: v }),
  myListing: () => apiFetch<Venue & { favourites: number }>('/venue/listing'),
  updateListing: (patch: Partial<Venue>) => apiFetch<Venue>('/venue/listing', { method: 'PATCH', body: patch }),
  events: () => apiFetch<Event[]>('/venue/events'),
  invoices: () => apiFetch<Invoice[]>('/venue/invoices'),
  downloadInvoicePdf: async (id: string, filename: string) => {
    const res = await fetch(`${API_URL}/venue/invoices/${id}/pdf`, { headers: getToken() ? { Authorization: `Bearer ${getToken()}` } : {} });
    if (!res.ok) throw new ApiError(res.status, 'ERROR', 'Failed to download PDF');
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  },
  subscription: subscriptionApi('venue'),
  // ---- hosting (opt-in, admin-gated — see VenueService doc comments) ----
  hostingStatus: () => apiFetch<{ hostingEnabled: boolean; request: VenueHostingRequest | null }>('/venue/hosting'),
  requestHosting: () => apiFetch<VenueHostingRequest>('/venue/hosting/request', { method: 'POST' }),
  hostedEvents: () => apiFetch<Event[]>('/venue/hosting/events'),
  upsertHostedEvent: (e: {
    id?: string; title: string; description?: string; category?: string; subCategory?: string; ageLimit?: string;
    tags?: string[]; date?: string; durationHrs?: number; organizerId?: string | null; status?: 'draft' | 'pending';
    conditions?: string[]; rules?: unknown; lineup?: unknown; seo?: unknown; promoterConfig?: unknown;
    posterUrl?: string | null; galleryUrls?: string[]; teaserVideoUrl?: string | null; socialBanners?: { postUrl?: string; storyUrl?: string };
    tiers?: { id?: string; name: string; price: number; quantity: number; includes?: string[]; description?: string }[];
  }) => apiFetch<Event>('/venue/hosting/events', { body: e }),
  deleteHostedEvent: (id: string) => apiFetch<{ ok: true }>(`/venue/hosting/events/${id}`, { method: 'DELETE' }),
  myLedger: () => apiFetch<{ balance: number; transactions: VenueLedgerTx[] }>('/venue/hosting/ledger'),
  withdraw: (amount: number) => apiFetch<{ ok: true }>('/venue/hosting/withdraw', { body: { amount } }),
  collaboratorOptions: () => apiFetch<VenueCollaboratorOption[]>('/venue/hosting/collaborator-options'),
  // ---- gate ops for hosted events — mirrors the organizer.* versions
  // above exactly, same real shapes, just scoped to this venue's own
  // hosted events instead of an organizer's ----
  attendees: (eventId: string) => apiFetch<OrgAttendee[]>(`/venue/hosting/events/${eventId}/attendees`),
  bookings: () => apiFetch<OrgBooking[]>('/venue/hosting/bookings'),
  marketing: marketingApi('/venue/hosting/marketing'),
  abandonedCarts: () => apiFetch<CartRecord[]>('/venue/hosting/carts'),
  remindCart: (id: string) => apiFetch<void>(`/venue/hosting/carts/${id}/remind`, { method: 'POST' }),
  guestList: (eventId: string) => apiFetch<{ entries: OrgGuestListEntry[]; namesCount: number; totalHeads: number; arrived: number }>(`/venue/hosting/events/${eventId}/guest-list`),
  addGuestListEntry: (eventId: string, body: { name: string; phone: string; plusOnes?: number; companions?: { name: string; phone: string }[] }) =>
    apiFetch<OrgGuestListEntry>(`/venue/hosting/events/${eventId}/guest-list`, { body }),
  toggleGuestArrived: (id: string) => apiFetch<OrgGuestListEntry>(`/venue/hosting/guest-list/${id}/toggle-arrived`, { method: 'POST' }),
  removeGuestListEntry: (id: string) => apiFetch<{ ok: true }>(`/venue/hosting/guest-list/${id}`, { method: 'DELETE' }),
  promoterGuests: (eventId: string) => apiFetch<OrgPromoterGuest[]>(`/venue/hosting/events/${eventId}/promoter-guests`),
  promoters: () =>
    apiFetch<
      {
        promoterId: string; promoterSlug: string; promoterName: string; city: string; bio: string; contact: string | null; verified: boolean;
        bankName: string | null; bankAccountNumber: string | null; bankLast4: string | null; accountHolderName: string | null; ifsc: string | null;
        eventCount: number; totalOwed: number; pendingEvents: number;
      }[]
    >('/venue/hosting/promoters'),
  coupons: () => apiFetch<Coupon[]>('/venue/hosting/coupons'),
  upsertCoupon: (c: Partial<Coupon>) => apiFetch<Coupon>('/venue/hosting/coupons', { body: c }),
  // ---- payment profiles (bank accounts to withdraw hosting revenue to) —
  // self-serve, no admin review, plural. Mirrors organizer.paymentProfiles.
  paymentProfiles: () => apiFetch<VenuePaymentProfile[]>('/venue/hosting/payment-profiles'),
  createPaymentProfile: (data: {
    legalName: string; businessAddress: string; country?: string; state?: string; city?: string; pincode?: string;
    bankAccountNumber: string; accountHolderName: string; ifsc: string; branch?: string;
    pan: string; gstin?: string; noGst?: boolean;
  }) => apiFetch<VenuePaymentProfile>('/venue/hosting/payment-profiles', { body: data }),
  updatePaymentProfile: (id: string, data: Partial<{
    legalName: string; businessAddress: string; country: string; state: string; city: string; pincode: string;
    bankAccountNumber: string; accountHolderName: string; ifsc: string; branch: string;
    pan: string; gstin: string; noGst: boolean;
  }>) => apiFetch<VenuePaymentProfile>(`/venue/hosting/payment-profiles/${id}`, { method: 'PATCH', body: data }),
  deletePaymentProfile: (id: string) => apiFetch<{ ok: true }>(`/venue/hosting/payment-profiles/${id}`, { method: 'DELETE' }),
  setDefaultPaymentProfile: (id: string) => apiFetch<{ ok: true }>(`/venue/hosting/payment-profiles/${id}/default`, { method: 'POST' }),
  live: (eventId: string) => apiFetch<OrgLiveMonitor>(`/venue/hosting/events/${eventId}/live`),
  manualCheckIn: (eventId: string, name: string, count?: number) => apiFetch<unknown>(`/venue/hosting/events/${eventId}/check-in`, { body: { name, count } }),
  setSalesPaused: (eventId: string, paused: boolean) => apiFetch<Event>(`/venue/hosting/events/${eventId}/pause-sales`, { method: 'PATCH', body: { paused } }),
};

// ---------- venue hosting team & roles — mirrors orgTeam/orgRoles exactly,
// scoped to the venue's Organizer panel instead of an organizer ----------
export interface VenueStaffMember {
  id: string;
  name: string;
  phone: string | null;
  roleName: string;
  scan: boolean;
  createdAt: string;
}
export interface VenueTeamAccess {
  venueId: string;
  venueBrand: string;
  venueLogoUrl: string | null;
  roleName: string;
  permissions: OrgModulePerms;
  scan: boolean;
}
export const venueTeam = {
  mine: () => apiFetch<VenueTeamAccess | null>('/venue/team/mine'),
  listStaff: () => apiFetch<VenueStaffMember[]>('/venue/team'),
  addStaff: (body: { name: string; phone: string; email?: string; roleName?: string; scan?: boolean }) => apiFetch<VenueStaffMember>('/venue/team', { body }),
  updateStaffRole: (id: string, roleName: string) => apiFetch<VenueStaffMember>(`/venue/team/${id}/role`, { body: { roleName } }),
  removeStaff: (id: string) => apiFetch<{ ok: true }>(`/venue/team/${id}`, { method: 'DELETE' }),
};
export const venueRoles = {
  list: () => apiFetch<Record<string, OrgModulePerms>>('/venue/roles'),
  add: (name: string) => apiFetch<unknown>('/venue/roles', { body: { name } }),
  setPerm: (name: string, module: string, key: OrgPermKey, value: boolean) =>
    apiFetch<unknown>(`/venue/roles/${encodeURIComponent(name)}/perm`, { body: { module, key, value } }),
  remove: (name: string) => apiFetch<{ ok: true }>(`/venue/roles/${encodeURIComponent(name)}`, { method: 'DELETE' }),
};
